import { Component, OnInit, OnDestroy, inject, signal, effect, untracked, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ComparisonType,
  DatePickerComponent,
  DateRangePickerOutput,
  DateRangePickerValue
} from '../../../../shared/components/date-picker/date-picker.component';
import { DropdownComponent } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { MultiStackedBarChartComponent } from '../../../../shared/components/charts/bar-charts/multi-stacked-bar-chart/multi-stacked-bar-chart.component';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import {
  getVisionboardCurrencyInfo,
  VISIONBOARD_CURRENCY_OPTIONS
} from '../../visionboard/shared/visionboard-currency.config';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

@Component({
  selector: 'app-fm-dashboard-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiStackedBarChartComponent,
    HorizontalBarChartComponent,
    DatePickerComponent,
    DropdownComponent,
    IconComponentV2,
    IndividualToasterComponent,
    PaginationComponent
  ],
  templateUrl: './fm-dashboard-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fm-dashboard-overview.component.scss']
})
export class FmDashboardOverviewComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private unitService = inject(UnitSuggestionService);
  private loaderService = inject(LoaderService);
  private fetchSubscription?: Subscription;
  /** Avoid fetching with component defaults before the date picker emits its range. */
  private datesReady = false;
  private fetchRequestId = 0;
  private lastDateRangeKey = '';
  /** Skip one effect-driven fetch when onDateRangeChange already triggered it. */
  private skipNextEffectFetch = false;
  private initialLoadDone = false;

  // Date State — synced from date picker (same as monthly-report-1)
  start_date = '';
  end_date = '';
  prev_start_date = '';
  prev_end_date = '';
  comparisonType: ComparisonType = 'none';
  unit = signal<string>('day');

  currencyOptions = VISIONBOARD_CURRENCY_OPTIONS;
  selectedCurrency = signal<string>('1');
  selectedCurrencyInfo = computed(() => getVisionboardCurrencyInfo(this.selectedCurrency()));

  productConfig = computed(() => {
    const productId = this.apiService.selectedProductId();
    if (!productId) return null;
    return this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
  });

  // Loading State
  isLoading = signal(true);

  // Data signals matching HTML
  concentrationRisk: any = {};
  growthLever: any = {};
  profitProtection: any = {};
  kpis: any = {
    totalFinanced: {},
    revenueCollected: {},
    activeBusinesses: {},
    totalContracts: {},
    activationRate: {},
    topIndustryShare: {}
  };
  topMarkets: any = {
    chartTitle: '',
    chartSubtitle: '',
    chartFootnote: '',
    chartData: [],
    emerging: { marketCount: 0, share: 0, rows: [] }
  };
  drivingNumbers: any = {
    bestIndustry: null,
    worstIndustry: null,
    contractMixSubtitle: ''
  };

  // Charts
  contractTypeMixChart: any = null;
  activeBizChart: any = null;
  waterfallChart: any = null;
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];

  // Critical Business Summary & Details
  isCriticalBusinessLoading = signal(false);
  criticalBusinessData = signal<any>(null);
  isRiskModalOpen = signal(false);
  riskSearchQuery = signal<string>('');
  selectedRiskTier = signal<string>('All');

  filteredRiskDetails = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details) return [];

    let list = data.details as any[];

    // Filter by search query
    const query = this.riskSearchQuery().toLowerCase().trim();
    if (query) {
      list = list.filter(item =>
        (item.practice_name || '').toLowerCase().includes(query) ||
        (item.business_owner || '').toLowerCase().includes(query)
      );
    }

    // Filter by active tier tab
    const tier = this.selectedRiskTier();
    if (tier !== 'All') {
      list = list.filter(item =>
        (item.churn_risk || '').toLowerCase() === tier.toLowerCase()
      );
    }

    return list;
  });

  riskCurrentPage = signal<number>(1);
  riskPageSize = signal<number>(10);

  riskTotalPages = computed(() => {
    const total = this.filteredRiskDetails().length;
    return Math.max(1, Math.ceil(total / this.riskPageSize()));
  });

  paginatedRiskDetails = computed(() => {
    const list = this.filteredRiskDetails();
    const page = this.riskCurrentPage();
    const size = this.riskPageSize();
    const start = (page - 1) * size;
    return list.slice(start, start + size);
  });

  paginationText = computed(() => {
    const total = this.filteredRiskDetails().length;
    const page = this.riskCurrentPage();
    const size = this.riskPageSize();
    const start = total === 0 ? 0 : (page - 1) * size + 1;
    const end = Math.min(page * size, total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  totalActiveContracts = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details) return 0;
    return data.details.reduce((sum: number, item: any) => sum + (Number(item.active_contracts) || 0), 0);
  });

  totalContractsCount = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details) return 0;
    return data.details.reduce((sum: number, item: any) => sum + (Number(item?.total_contracts) || 0), 0);
  });

  atRiskBusinessesCount = computed(() => {
    const data = this.criticalBusinessData();
    if (!data) return 0;
    return (Number(data.high_risk) || 0) + (Number(data.critical_risk) || 0);
  });

  avgDaysSinceContact = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details || data.details.length === 0) return 0;
    const totalDays = data.details.reduce((sum: number, item: any) => sum + (Number(item.days_since_last_contract) || 0), 0);
    return Math.round(totalDays / data.details.length);
  });

  constructor() {
    effect(() => {
      const pid = this.apiService.selectedProductId();
      if (pid && this.datesReady) {
        untracked(() => {
          if (this.skipNextEffectFetch) {
            this.skipNextEffectFetch = false;
            return;
          }
          this.fetchData();
        });
      }
    });
  }

  ngOnInit() {
    // Initial fetch is driven by date picker `dateRangeChange` (same as monthly-report-1)
  }

  ngOnDestroy() {
    this.fetchSubscription?.unsubscribe();
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    const rangeKey = [
      event.start_date,
      event.end_date,
      event.comparison_type,
      event.previous_start_date ?? '',
      event.previous_end_date ?? '',
      event.unit
    ].join('|');

    if (rangeKey === this.lastDateRangeKey) return;
    this.lastDateRangeKey = rangeKey;

    this.start_date = this.normalizeDate(event.start_date);
    this.end_date = this.normalizeDate(event.end_date);
    this.comparisonType = event.comparison_type;

    if (event.comparison_type !== 'none' && event.previous_start_date && event.previous_end_date) {
      this.prev_start_date = this.normalizeDate(event.previous_start_date);
      this.prev_end_date = this.normalizeDate(event.previous_end_date);
    } else {
      this.prev_start_date = '';
      this.prev_end_date = '';
    }

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date === this.end_date) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit || this.unitService.getUnitSuggestions(this.start_date, this.end_date).recommended as any);
    this.datesReady = true;
    this.skipNextEffectFetch = true;
    this.fetchData();
  }

  rangeValues(_event: DateRangePickerValue) {
    // Kept for parity with monthly-report-1 date picker wiring
  }

  onCurrencyChange(value: string) {
    if (value === this.selectedCurrency()) return;
    this.selectedCurrency.set(value);
    if (this.datesReady) {
      this.fetchData();
    }
  }

  private normalizeDate(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  private getPayloadExtras(): Record<string, any> {
    const productId = this.apiService.selectedProductId();
    if (!productId) return {};
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    return productConfig.payloadExtras ?? {};
  }

  private buildPayload(base: Record<string, any>): Record<string, any> {
    const payload: Record<string, any> = {
      ...base,
      ...this.getPayloadExtras()
    };

    if (this.productConfig()?.productServerName === 'credee') {
      const { cocu_id, country_id } = this.selectedCurrencyInfo();
      payload['cocu_id'] = cocu_id;
      payload['country_id'] = country_id;
    }

    return payload;
  }





  private makeApiCall(endpoint: string, payload: any, isStats: boolean = false) {
    const productId = this.apiService.selectedProductId();
    if (!productId) return of(null);
    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const basePath = isStats ? 'visionboard' : (productConfig.reportToolsPath || 'visionboard/tools');
    const url = `${basePath}/${endpoint}`;

    const options = {
      showLoader: false,
      showErrorToaster: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    return this.apiService.post(url, payload, options).pipe(
      catchError(err => {
        console.error(`Error fetching ${url}:`, err);
        return of(null);
      })
    );
  }

  private fetchData() {
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.start_date || !this.end_date) return;

    const requestId = ++this.fetchRequestId;
    this.fetchSubscription?.unsubscribe();
    this.isLoading.set(true);
    if (!this.initialLoadDone) {
      this.loaderService.show();
    }

    this.fetchCriticalBusinessData();

    // FM Visionboard Payloads
    const payloadCurrent = this.buildPayload({
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      overall: 0
    });

    const payloadOverall = this.buildPayload({
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      overall: 1
    });

    // Prism API Payloads
    const prismPayload = {
      min_date_added: this.start_date,
      max_date_added: this.end_date,
      unit: this.unit(),
      overall: 1
    };

    const diffDays = dayjs(this.end_date).diff(dayjs(this.start_date), 'day') + 1;
    const effPrevStart = (this.comparisonType !== 'none' && this.prev_start_date)
      ? this.prev_start_date
      : dayjs(this.start_date).subtract(diffDays, 'day').format('YYYY-MM-DD');
    const effPrevEnd = (this.comparisonType !== 'none' && this.prev_end_date)
      ? this.prev_end_date
      : dayjs(this.end_date).subtract(diffDays, 'day').format('YYYY-MM-DD');

    const prismPayloadPrev = {
      min_date_added: effPrevStart,
      max_date_added: effPrevEnd,
      unit: this.unit(),
      overall: 0
    };

    const includeComparison = this.comparisonType !== 'none'
      && !!this.prev_start_date
      && !!this.prev_end_date;

    const requests: Record<string, any> = {
      // --- FM Visionboard APIs ---
      bizContracts: this.makeApiCall('business_contracts_overall', payloadCurrent, false),
      bizTrainingStats: this.makeApiCall('business_training_stats', payloadCurrent, false),
      subInd1: this.makeApiCall('sub_industries_contracts', { ...payloadCurrent, type: [0] }, false), // TBC on coverage
      ...(includeComparison ? { subInd1Prev: this.makeApiCall('sub_industries_contracts', { min_date_added: effPrevStart, max_date_added: effPrevEnd, unit: this.unit(), overall: 0, type: [0] }, false) } : {}),
      contractTypesCount: this.makeApiCall('contract_types_count', payloadCurrent, false),
      activeBizStats: this.makeApiCall('active_business_stats', payloadCurrent, false),
      contractsCreated: this.makeApiCall('contracts_created_running_total', payloadCurrent, false),
      activeBiz: this.makeApiCall('active_business', prismPayload, true),
      totalBiz: this.makeApiCall('total_business', prismPayload, true),

      // --- Prism APIs (Now exposed as standard Visionboard endpoints) ---
      totalFinanced: this.makeApiCall('total_financed_overall', prismPayload, false),
      revenueCollected: this.makeApiCall('revenue_collected_overall', prismPayload, false),
      prevRevenueCollectedOverall: this.makeApiCall('revenue_collected_overall', prismPayloadPrev, false),
      bizByContracts: this.makeApiCall('businesses_by_contracts', { ...prismPayload, top_n: 6 }, false),
      stateContracts: this.makeApiCall('state_contracts', { ...prismPayload, top_n: 7 }, false),
      netRevenueWaterfall: this.makeApiCall('net_revenue_waterfall', prismPayload, false),
    };

    this.fetchSubscription = forkJoin(requests).subscribe({
      next: (res: any) => {
        if (requestId !== this.fetchRequestId) return;
        this.processData(res, includeComparison);
        this.isLoading.set(false);
        if (!this.initialLoadDone) {
          this.loaderService.hide();
          this.initialLoadDone = true;
        }
      },
      error: (err) => {
        console.error('Failed to fetch dashboard overview data', err);
        if (requestId === this.fetchRequestId) {
          this.isLoading.set(false);
        }
        if (!this.initialLoadDone) {
          this.loaderService.hide();
          this.initialLoadDone = true;
        }
      }
    });
  }

  private formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  }

  get hasComparison(): boolean {
    return this.comparisonType !== 'none';
  }

  get comparisonLabel(): string {
    switch (this.comparisonType) {
      case 'previous-period': return 'Prev';
      case 'previous-month': return 'MoM';
      case 'previous-year': return 'YoY';
      case 'custom': return 'Custom';
      default: return 'MoM';
    }
  }

  /** Matches contract type mix barHeight (30) so the x-axis fits inside the card. */
  getContractTypeMixChartMinHeight(): number {
    const count = this.contractTypeMixChart?.length ?? 0;
    return count > 0 ? count * 28 : 120;
  }

  /** Adequate left margin to fit full Payment Plan names without clipping. */
  getContractTypeMixLeftMargin(): number {
    return 120;
  }

  /** X-axis ceiling rounded to nearest 5%, matching HTML reference scale. */
  getContractTypeMixMaxVal(): number {
    const chart = this.contractTypeMixChart;
    if (!chart?.length) return 40;
    const maxVal = Math.max(...chart.map((d: { value: number }) => d.value));
    return Math.ceil(maxVal / 5) * 5 || 5;
  }

  /** Top markets — tight left margin from label length (country names are short). */
  getTopMarketsLeftMargin(): number {
    const labels = this.topMarkets.chartData?.map((d: { label: string }) => d.label) ?? [];
    if (!labels.length) return 80;
    const maxChars = Math.max(...labels.map((l: string) => l.length));
    return Math.min(150, Math.max(80, maxChars * 6.5 + 16));
  }

  getTopMarketsChartMinHeight(): number {
    const count = this.topMarkets.chartData?.length ?? 0;
    return count > 0 ? count * 56 : 112;
  }

  /** Contract-count x-axis ceiling (e.g. 205 → 250). */
  getTopMarketsMaxVal(): number {
    const chart = this.topMarkets.chartData;
    if (!chart?.length) return 50;
    const maxVal = Math.max(...chart.map((d: { value: number }) => d.value));
    if (maxVal <= 10) return Math.ceil(maxVal) || 1;
    if (maxVal <= 100) return Math.ceil(maxVal / 10) * 10;
    if (maxVal <= 1000) return Math.ceil(maxVal / 100) * 100;
    return Math.ceil(maxVal / 500) * 500;
  }
  getWaterfallLeftMargin(): number { return 120; }
  getWaterfallChartMinHeight(): number { return 128; }
  getWaterfallMaxVal(): number {
    const chart = this.waterfallChart?.data;
    if (!chart?.length) return 50000;
    const maxVal = Math.max(...chart.map((d: { value: number }) => Math.abs(d.value)));
    if (maxVal <= 1000) return Math.ceil(maxVal / 100) * 100 || 100;
    if (maxVal <= 10000) return Math.ceil(maxVal / 1000) * 1000;
    if (maxVal <= 100000) return Math.ceil(maxVal / 20000) * 20000;
    return Math.ceil(maxVal / 50000) * 50000;
  }

  getWaterfallTickStep(): number {
    const maxVal = this.getWaterfallMaxVal();
    let step = 10;
    if (maxVal > 200) {
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
      step = magnitude;
      if (maxVal / step < 3) step = magnitude / 2;
      if (maxVal / step > 8) step = magnitude * 2;
    }
    return step;
  }

  getWaterfallMinVal(): number {
    const chart = this.waterfallChart?.data;
    if (!chart?.length) return -50000;

    const minDataVal = Math.min(0, ...chart.map((d: { value: number }) => d.value));
    const step = this.getWaterfallTickStep();

    return Math.floor(minDataVal / step) * step;
  }

  getWaterfallTickValues(): number[] {
    const step = this.getWaterfallTickStep();
    const maxVal = this.getWaterfallMaxVal();
    const minVal = this.getWaterfallMinVal();
    const ticks: number[] = [];

    // Ticks going backwards from 0
    for (let i = 0; i >= minVal; i -= step) {
      if (!ticks.includes(i)) ticks.push(i);
    }

    // Explicitly add minVal so it shows on the axis if it's not a multiple of step
    if (minVal < 0 && !ticks.includes(minVal)) {
      ticks.push(minVal);
    }

    // Ticks going forwards from 0
    for (let i = step; i <= maxVal; i += step) {
      if (!ticks.includes(i)) ticks.push(i);
    }

    return ticks.sort((a, b) => a - b);
  }
  get formattedDateRange(): string { return ''; }
  shouldEnableScroll(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return end.diff(start, 'day') > 365;
  }

  private getContractTypeLabel(item: any): string { return item?.contract_type_label || 'Unknown'; }

  private processData(res: any, includeComparison = false) {
    console.log('FM Dashboard API response:', res);
    // Basic Parsing of FM API structures
    const bizContracts = res.bizContracts?.data || {};
    const bizTraining = res.bizTrainingStats?.data || {};
    const industryContracts = res.subInd1?.data || res.subInd1 || res.industryContracts?.data || [];

    // TBC fields for Prism APIs
    const totalFinancedData = res.totalFinanced?.data || res.totalFinanced?.stats || res.totalFinanced?.records?.stats || res.totalFinanced || {};
    const revCollectedData = res.revenueCollected?.data || {};

    // 2.1 Decisions on the table

    // 1. Concentration Risk (Red) -> from sub_industries_contracts
    const highestOverdueIndustry = industryContracts.reduce((worst: any, row: any) => {
      if (!worst) return row;
      return (Number(row.overdue_ratio || 0) > Number(worst.overdue_ratio || 0)) ? row : worst;
    }, null) || industryContracts[0];

    // Fallback values if API is empty
    const concIndName = highestOverdueIndustry?.ind_name || highestOverdueIndustry?.speciality_name || 'Consultant';
    const totalIndustryContracts = industryContracts.reduce((sum: number, row: any) => sum + (Number(row.total_contracts) || 0), 0);
    const rawBookPct = highestOverdueIndustry?.total_contracts || 0;
    const concBookPct = Math.max(0, rawBookPct);
    const concOverdue = Math.min(100, Math.max(0, highestOverdueIndustry?.overdue_ratio ?? 0));
    const concDelinquent = highestOverdueIndustry ? Math.max(0, Math.round((highestOverdueIndustry.total_contracts * concOverdue) / 100)) : 2890;

    this.concentrationRisk = {
      name: concIndName,
      bookPct: concBookPct,
      overdue: concOverdue,
      delinquent: concDelinquent
    };

    // 2. Growth Lever (Orange) -> Activation gap (business_contracts_overall)
    const totalBiz = Math.max(0, bizContracts.total_businesses || 0);
    const actRate = Math.max(0, bizContracts.activation_rate_new_signup_pct || bizContracts.activation_rate_pct || 0);
    const newSignupActivated = Math.round(totalBiz * (actRate / 100));
    const inactiveCount = Math.abs((bizContracts.total_businesses || 0) - (bizContracts.businesses_with_contracts || 0));

    this.growthLever = {
      inactive: inactiveCount,
      enrolled: totalBiz,
      activationRate: actRate,
      targetRate: 45,
      upside: Math.max(0, Math.round((totalBiz || 970) * (45 - actRate) / 100)) || 125
    };

    // 3. Profit Protection (Green) -> from total_financed_overall API (res.totalFinanced)
    const tf = res.totalFinanced?.data || res.totalFinanced?.stats || res.totalFinanced?.records?.stats || res.totalFinanced || {};
    const refundsAmt = Number(tf.refunds_amount ?? tf.total_refunds ?? tf.refunds ?? tf.refund ?? 0);
    const reversalsAmt = Number(tf.reversals_amount ?? tf.total_reversals ?? tf.reversals ?? tf.reversal ?? 0);
    const tfLeakage = (Math.abs(refundsAmt) + Math.abs(reversalsAmt)) || Number(tf.leakage ?? tf.total_deductions ?? tf.leakage_amount ?? tf.refund_and_reversal_amount ?? 0);
    const leakageAmt = tfLeakage || Math.max(0, res.netRevenueWaterfall?.data?.waterfall?.total_deductions ?? 275000);
    const netRev = Math.max(0, res.netRevenueWaterfall?.data?.waterfall?.net_revenue ?? 1430000);
    this.profitProtection = {
      leakage: leakageAmt,
      net: netRev,
      recoverable: Math.max(0, Math.round(leakageAmt / 2))
    };

    // 2.2 Where the book stands
    const totalFinancedVal = totalFinancedData.total_financed_amount || 0;
    const totalContractsVal = totalFinancedData.total_contracts ?? bizContracts.total_contracts ?? 0;
    const avgPerContractVal = totalContractsVal ? totalFinancedVal / totalContractsVal : 0;

    this.kpis.totalFinanced = {
      value: totalFinancedVal,
      meta: `Avg per contract $${this.formatNumber(Math.round(avgPerContractVal))} · across ${this.formatNumber(totalFinancedData.active_contracts ?? 0)} active contracts`,
      mom: totalFinancedData.mom_growth_percent || 0
    };

    const formatCompact = (val: number) => {
      if (!val) return '$0';
      if (val >= 1000000) return '$' + (val / 1000000).toFixed(2).replace(/\.00$/, '') + 'M';
      if (val >= 1000) return '$' + (val / 1000).toFixed(0) + 'K';
      return '$' + val;
    };

    const upf = revCollectedData.split?.upfront_fee || 0;
    const enr = revCollectedData.split?.enrollment_fee || 0;
    const lat = revCollectedData.split?.late_fee || 0;

    const totalRevenue = Number(revCollectedData.total_revenue || 0);
    let revenueGrowth = Number(revCollectedData.mom_growth_percent || 0);
    if (res.prevRevenueCollectedOverall != null) {
      const prevRevData = res.prevRevenueCollectedOverall?.data || res.prevRevenueCollectedOverall?.stats || res.prevRevenueCollectedOverall || {};
      const prevTotalRevenue = Number(prevRevData.total_revenue ?? prevRevData.totalRevenue ?? 0);
      if (prevTotalRevenue > 0) {
        revenueGrowth = ((totalRevenue - prevTotalRevenue) / prevTotalRevenue) * 100;
      }
    }

    this.kpis.revenueCollected = {
      value: totalRevenue,
      meta: `Upfront ${formatCompact(upf)} · Enroll ${formatCompact(enr)} · Late ${formatCompact(lat)}`,
      mom: revenueGrowth
    };

    const abStats = (res.activeBiz?.data?.stats?.dental !== undefined) ? res.activeBiz.data.stats :
      (res.activeBiz?.data?.dental !== undefined) ? res.activeBiz.data :
        (res.activeBiz?.dental !== undefined) ? res.activeBiz :
          res.activeBizStats?.data?.this_unit || {};

    const tbStats = (res.totalBiz?.data?.stats?.all_time_business !== undefined) ? res.totalBiz.data.stats :
      (res.totalBiz?.data?.all_time_business !== undefined) ? res.totalBiz.data :
        (res.totalBiz?.all_time_business !== undefined) ? res.totalBiz :
          res.activeBizStats?.data?.this_unit || {};

    this.kpis.activeBusinesses = {
      value: res.activeBizStats?.data?.this_unit?.total || abStats.total || 0,
      meta: `All-time ${this.formatNumber(tbStats.all_time_business || abStats.all_time_business || 0)} · Dental ${this.formatNumber(abStats.dental || 0)} · HC ${this.formatNumber(abStats.healthcare || 0)} · Non-HC ${this.formatNumber(abStats.non_healthcare || 0)}`,
      mom: res.activeBizStats?.data?.this_unit?.growth || 0
    };

    // --- Market Footprint (State Contracts) ---
    const stateDataObj = res.stateContracts?.result || res.stateContracts?.data || res.stateContracts || {};
    let scData = stateDataObj.states;
    if (!Array.isArray(scData)) {
      scData = Array.isArray(stateDataObj.data) ? stateDataObj.data : (Array.isArray(stateDataObj) ? stateDataObj : []);
    }

    const allTimeContracts = totalFinancedData.total_overall_contracts || totalFinancedData.total_contracts_overall || totalFinancedData.all_time_contracts || totalFinancedData.total_contracts || bizContracts.total_contracts || 0;
    const activeMarketsCount = scData.length || 0;

    let topContractsMeta = '';
    const ctRes = res.contractTypesCount;
    const contractTypesData = Array.isArray(ctRes?.unit_data) ? ctRes.unit_data :
      (Array.isArray(ctRes?.data?.unit_data) ? ctRes.data.unit_data : []);

    if (contractTypesData.length > 0) {
      const sortedContractTypes = [...contractTypesData].sort((a: any, b: any) => (Number(b.count) || 0) - (Number(a.count) || 0));
      const totalContractTypesCount = sortedContractTypes.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

      if (totalContractTypesCount > 0) {
        const top1 = sortedContractTypes[0];
        const topCount = Number(top1?.count) || 0;
        const names = top1 ? this.getContractTypeLabel(top1) : '';

        const percentage = Math.round((topCount / totalContractTypesCount) * 100);
        topContractsMeta = ` · ${percentage}% are ${names}`;
      }
    }

    this.kpis.totalContracts = {
      value: totalFinancedData.total_contracts ?? bizContracts.total_contracts ?? 0,
      meta: `All-time ${this.formatNumber(allTimeContracts)} ${topContractsMeta}`,
      mom: totalFinancedData.contracts_mom_growth_percent ?? totalFinancedData.total_contracts_mom_growth_percent ?? totalFinancedData.mom_growth_percent ?? 0
    };

    this.kpis.activationRate = {
      value: bizContracts.activation_rate_pct || 0,
      target: 70,
      meta: `${this.formatNumber(bizContracts.businesses_with_contracts || 0)} of ${this.formatNumber(bizContracts.total_businesses || 0)} enrolled wrote a contract · ${this.formatNumber(Math.abs((bizContracts.total_businesses || 0) - (bizContracts.businesses_with_contracts || 0)))} have not`,
      mom: 0
    };

    const subIndData = res.subInd1?.data?.length ? res.subInd1.data : industryContracts;

    const totalContractsSum = subIndData.reduce((sum: number, row: any) => sum + (Number(row.total_contracts) || 0), 0);
    const topSpecialty = subIndData.reduce((max: any, row: any) =>
      (Number(row.total_contracts) > (Number(max?.total_contracts) || 0)) ? row : max, null
    );

    const topShare = totalContractsSum && topSpecialty
      ? (topSpecialty.total_contracts / totalContractsSum) * 100
      : 0;

    const rawPrev = res.subInd1Prev;
    const industryContractsPrev = includeComparison
      ? (Array.isArray(rawPrev?.data) ? rawPrev.data :
        Array.isArray(rawPrev) ? rawPrev : [])
      : [];
    // console.log('[FM] subInd1Prev raw:', rawPrev, 'parsed:', industryContractsPrev);

    const topSpecialtyPrev = industryContractsPrev.reduce((max: any, row: any) =>
      (Number(row.total_contracts) > (Number(max?.total_contracts) || 0)) ? row : max, null
    );
    const prevTopContracts = Number(topSpecialtyPrev?.total_contracts) || 0;
    const currTopContracts = Number(topSpecialty?.total_contracts) || 0;
    //  const momTopShare = includeComparison && prevTopContracts > 0
    //   ? ((currTopContracts - prevTopContracts) / prevTopContracts) * 100
    //   : 0;
    const momTopShare = topSpecialty?.overdue_ratio ? Number(topSpecialty.overdue_ratio) : 0;
    // console.log('[FM] topIndustryShare mom:', { currTopContracts, prevTopContracts, momTopShare, includeComparison });

    this.kpis.topIndustryShare = {
      value: topShare,
      meta: `${topSpecialty?.speciality_name || 'N/A'} · ${this.formatNumber(topSpecialty?.total_contracts || 0)} of ${this.formatNumber(totalContractsSum)} contracts`,
      mom: momTopShare
    };

    // stateDataObj and scData are declared and initialized above

    const totalStateContracts = stateDataObj.total_contracts || scData.reduce((sum: number, c: any) => sum + (Number(c.contract_count || c.count || c.total_contracts || c.total) || 0), 0);
    const marketBarColors = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#a855f7', '#64748b'];

    // Separate regular states and any pre-computed 'other' bucket from API
    const regularStates = scData.filter((c: any) => !c.is_other && c.state !== 'Other' && c.state_code !== 'OTHER');
    const otherBucketFromApi = scData.find((c: any) => c.is_other || c.state === 'Other' || c.state_code === 'OTHER');

    // Keep regular states sorted by contract count descending
    const sortedRegular = [...regularStates].sort((a: any, b: any) => {
      const valA = Number(a.contract_count || a.count || a.total_contracts || a.total) || 0;
      const valB = Number(b.contract_count || b.count || b.total_contracts || b.total) || 0;
      return valB - valA;
    });

    const topN = stateDataObj.top_n || 7;
    const topStates = sortedRegular.slice(0, topN);
    const leftOverStates = sortedRegular.slice(topN);

    let computedMaxVal = 100;
    const maxStateTotal = scData.length ? Math.max(...scData.map((c: any) => Number(c.contract_count || c.count || c.total_contracts || c.total) || 0)) : 0;
    if (maxStateTotal > 0) {
      if (maxStateTotal <= 10) computedMaxVal = Math.ceil(maxStateTotal) || 1;
      else if (maxStateTotal <= 100) computedMaxVal = Math.ceil(maxStateTotal / 10) * 10;
      else if (maxStateTotal <= 1000) computedMaxVal = Math.ceil(maxStateTotal / 100) * 100;
      else computedMaxVal = Math.ceil(maxStateTotal / 500) * 500;
    }

    const stateChartData: any[] = topStates.map((c, i) => {
      const val = Number(c.contract_count || c.count || c.total_contracts || c.total) || 0;
      const barColor = c.color || marketBarColors[i % marketBarColors.length] || '#6366f1';
      const label = c.state || c.state_name || c.name || 'Unknown';
      const share = totalStateContracts ? (val / totalStateContracts) * 100 : 0;
      return {
        label,
        value: val,
        share: share,
        displayValue: `${Number.isInteger(share) ? share : share.toFixed(1)}%`,
        color: barColor,
        segments: [
          { value: val, color: barColor, label },
          { value: Math.max(0, computedMaxVal - val), color: 'rgba(148, 163, 184, 0.2)', label: '<b class="hide-rem-row"></b><style>.horizontal-tooltip div:has(> div > span > .hide-rem-row) { display: none !important; }</style>' }
        ]
      };
    });

    if (otherBucketFromApi) {
      const val = Number(otherBucketFromApi.contract_count || otherBucketFromApi.count || otherBucketFromApi.total_contracts || otherBucketFromApi.total) || 0;
      const barColor = otherBucketFromApi.color || marketBarColors[7] || '#9ca3af';
      const label = otherBucketFromApi.state || otherBucketFromApi.state_name || 'Other';
      const share = totalStateContracts ? (val / totalStateContracts) * 100 : 0;
      stateChartData.push({
        label,
        value: val,
        share: share,
        displayValue: `${Number.isInteger(share) ? share : share.toFixed(1)}%`,
        color: barColor,
        segments: [
          { value: val, color: barColor, label },
          { value: Math.max(0, computedMaxVal - val), color: 'rgba(148, 163, 184, 0.2)', label: '<b class="hide-rem-row"></b><style>.horizontal-tooltip div:has(> div > span > .hide-rem-row) { display: none !important; }</style>' }
        ]
      });
    } else if (leftOverStates.length > 0) {
      const otherTotal = leftOverStates.reduce((sum: number, c: any) => sum + (Number(c.contract_count || c.count || c.total_contracts || c.total) || 0), 0);
      const barColor = marketBarColors[7] || '#64748b';
      const otherLabel = `Other ${leftOverStates.length} states`;
      const share = totalStateContracts ? (otherTotal / totalStateContracts) * 100 : 0;
      stateChartData.push({
        label: otherLabel,
        value: otherTotal,
        share: share,
        displayValue: `${Number.isInteger(share) ? share : share.toFixed(1)}%`,
        color: barColor,
        segments: [
          { value: otherTotal, color: barColor, label: otherLabel },
          { value: Math.max(0, computedMaxVal - otherTotal), color: 'rgba(148, 163, 184, 0.2)', label: '<b class="hide-rem-row"></b><style>.horizontal-tooltip div:has(> div > span > .hide-rem-row) { display: none !important; }</style>' }
        ]
      });
    }

    let chartSubtitle = stateDataObj.headline || '';
    if (!chartSubtitle) {
      if (topStates.length >= 2 && totalStateContracts > 0) {
        const top1 = topStates[0];
        const top2 = topStates[1];
        const top1Name = top1.state || top1.state_name || top1.name || 'Unknown';
        const top2Name = top2.state || top2.state_name || top2.name || 'Unknown';
        const top1Val = Number(top1.contract_count || top1.count || top1.total_contracts || top1.total) || 0;
        const top2Val = Number(top2.contract_count || top2.count || top2.total_contracts || top2.total) || 0;
        const top2Share = Math.round(((top1Val + top2Val) / totalStateContracts) * 100);

        chartSubtitle = `${top1Name} + ${top2Name} anchor ${top2Share}% of the book — top ${topStates.length} states + an 'Other' bucket for the remaining markets`;
      } else {
        chartSubtitle = `State share of contracts`;
      }
    }

    this.topMarkets = {
      chartTitle: stateDataObj.title || 'State Share of Contracts',
      chartSubtitle,
      chartFootnote: '',
      chartData: stateChartData.sort((a, b) => {
        if (a.label?.startsWith('Other') && !b.label?.startsWith('Other')) return 1;
        if (!a.label?.startsWith('Other') && b.label?.startsWith('Other')) return -1;
        return b.value - a.value;
      }),
      emerging: { marketCount: 0, share: 0, rows: [] }
    };

    const bestInd = industryContracts.reduce((best: any, row: any) => {
      if (row.total_contracts < 10) return best;
      if (!best) return row;
      const score = (row.total_contracts * row.average_financed_amount) * (100 - row.overdue_ratio);
      const bestScore = (best.total_contracts * best.average_financed_amount) * (100 - best.overdue_ratio);
      return score > bestScore ? row : best;
    }, null) || industryContracts[0];

    const getHighestRiskIndustry = (data: any[]) => {
      if (!data || data.length === 0) return null;
      const worst = data.reduce((currentWorst: any, row: any) => {
        if (Number(row.total_contracts) < 10) return currentWorst;
        if (!currentWorst) return row;
        return Number(row.overdue_ratio) > Number(currentWorst.overdue_ratio) ? row : currentWorst;
      }, null);
      return worst ?? data[0];
    };

    const worstInd = getHighestRiskIndustry(industryContracts);

    this.drivingNumbers = {
      bestIndustry: bestInd,
      worstIndustry: worstInd,
      contractMixSubtitle: ''
    };

    // --- 1. Contract Type Mix ---
    const ctData = res.contractTypesCount?.overall || res.contractTypesCount?.unit_data || res.contractTypesCount?.data || [];
    const ctTotal = ctData.reduce((sum: number, c: any) => sum + (Number(c.count) || 0), 0);
    const mixColors = ['#8b5cf6', '#d946ef', '#f97316', '#0ea5e9'];
    this.contractTypeMixChart = ctData.length ? ctData.map((c: any, i: number) => ({
      label: this.getContractTypeLabel(c),
      value: ctTotal ? ((Number(c.count) || 0) / ctTotal) * 100 : 0,
      color: mixColors[i % mixColors.length]
    })) : null;

    // --- 2. Active Businesses - industry split ---
    const abStatsData = res.activeBizStats?.data?.unit_data || [];
    const aggregated = new Map<string, any>();

    abStatsData.forEach((d: any) => {
      const dateStr = d.start || d.start_time || d.period || d.unit_date || d.date;
      const date = dayjs(dateStr);
      const isMultiYear = this.start_date && this.end_date && dayjs(this.start_date).year() !== dayjs(this.end_date).year();
      let format = isMultiYear ? 'MMM DD, YYYY' : 'MMM DD';
      if (this.unit() === 'year') {
        format = 'YYYY';
      } else if (this.unit() === 'month') {
        format = 'MMM YYYY';
      } else if (this.unit() === 'hour') {
        format = 'MMM DD, h A';
      }

      let label = date.format(format);
      if (this.unit() === 'week') {
        const endStr = d.end || d.end_time;
        const endDate = endStr ? dayjs(endStr) : date.add(6, 'day');
        label = isMultiYear ? `${date.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}` : `${date.format('DD MMM')} - ${endDate.format('DD MMM')}`;
      } else if (this.unit() === 'quarter') {
        const qNum = Math.floor(date.month() / 3) + 1;
        label = `Q${qNum} ${date.format('YYYY')}`;
      }

      if (!aggregated.has(label)) {
        aggregated.set(label, { label, dental: 0, healthcare: 0, non_healthcare: 0, timestamp: date.valueOf() });
      }
      const item = aggregated.get(label)!;
      item.dental += (Number(d.dental) || 0);
      item.healthcare += (Number(d.healthcare) || 0);
      item.non_healthcare += (Number(d.non_healthcare) || 0);
    });

    const abChartData = Array.from(aggregated.values()).sort((a, b) => a.timestamp - b.timestamp);

    this.activeBizChart = {
      data: abChartData,
      hasData: abChartData.some((d: any) => d.dental > 0 || d.healthcare > 0 || d.non_healthcare > 0),
      legend: [
        { key: 'non_healthcare', label: 'Non-Healthcare', color: '#9333ea' },
        { key: 'healthcare', label: 'Healthcare', color: '#3b82f6' },
        { key: 'dental', label: 'Dental', color: '#0d9488' }
      ]
    };

    // --- 3. Net Revenue Waterfall ---
    const wf = res.netRevenueWaterfall?.data?.waterfall || {};
    const wfData = [
      { label: 'Upfront', value: wf.upfront_collected || 0, color: '#9333ea' },
      { label: 'Enrollment', value: wf.enrollment_collected || 0, color: '#6366f1' },
      { label: 'Late Fee', value: wf.late_collected || 0, color: '#ea580c' },
      { label: 'Refunds', value: -(wf.total_refunds || 0), color: '#dc2626' },
      { label: 'Reversals', value: -(wf.total_reversals || 0), color: '#f97316' }
    ].sort((a, b) => b.value - a.value);
    this.waterfallChart = {
      data: wfData,
      hasData: wfData.some((d: any) => Math.abs(d.value) > 0),
      net: wf.net_revenue ? (wf.net_revenue / 1000000) : 0,
      leakage: wf.total_deductions || 0
    };
  }

  private fetchCriticalBusinessData() {
    this.isCriticalBusinessLoading.set(true);
    const payload = {
      ...(this.buildPayload({}) || {}),
      min_date_added: this.start_date,
      max_date_added: this.end_date,
      unit: this.unit(),
      overall: 0
    };
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };
    this.apiService.post('visionboard/get_critical_business', payload, options).subscribe({
      next: (res: any) => {
        if (res && res.result) {
          const currentResult = res.result;

          // MoM Calculation key
          const currentChurn = Number(currentResult.portfolio_churn_rate) || 0;
          const prevChurn = Number(currentResult.previous_portfolio_churn_rate) || 0;

          let churnGrowth = 0;
          if (prevChurn !== 0) {
            churnGrowth = ((currentChurn - prevChurn) / prevChurn) * 100;
          } else if (currentChurn !== 0) {
            churnGrowth = 100;
          }

          const growthPrefix = churnGrowth > 0 ? '+' : '';
          currentResult.churnGrowthText = `${growthPrefix}${churnGrowth.toFixed(1)}%`;

          this.criticalBusinessData.set(currentResult);
        }
        this.isCriticalBusinessLoading.set(false);
      },
      error: () => {
        this.isCriticalBusinessLoading.set(false);
      }
    });
  }

  openRiskModal() { this.isRiskModalOpen.set(true); }
  closeRiskModal() { this.isRiskModalOpen.set(false); }
  onRiskSearch(e: any) { this.riskSearchQuery.set(e.target.value); }
  onRiskTierChange(e: any) { this.selectedRiskTier.set(e.target.value); }
  changeRiskPage(p: number) { this.riskCurrentPage.set(p); }
  getDelinquentCount(ind: any): number {
    if (!ind || typeof ind.total_contracts !== 'number' || typeof ind.overdue_ratio !== 'number') return 0;
    return Math.round((ind.total_contracts * ind.overdue_ratio) / 100);
  }

  setRiskSearchQuery(query: string) { this.riskSearchQuery.set(query); }
  setRiskTier(tier: string) { this.selectedRiskTier.set(tier); }
  onRiskPageChange(page: number) { this.riskCurrentPage.set(page); }
  onRiskPageSizeChange(size: number) { this.riskPageSize.set(size); }
}





